import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { UIStyles, surfaces } from '../../theme'
import themeColors from '@layers-frontend/commons/styles/themeColors'
import LayiMark, { NAV_LINK_CLASS } from './LayiMark'

// The page switcher below the collapse width (the toggler passes the icon row's, so both switch
// together): a hamburger button in the icon row's place and, under the bar, the same entries as
// titled rows (icon + name), the current page in white. Another site's entry (Regional Intel)
// carries a ↗ and opens in a new tab; choosing it hands the focus back to the hamburger, as the
// page stays. Looks like the user menu (fixed under the 60px bar); closes on a choice, a click
// outside or Escape.

const TOP_BAR_HEIGHT = 60
const ACTIVE_CLASS = 'is-active'

// Size of an `svgIcon` in the menu: Regional Intel's outline at its own 28px box sits with the 22px glyphs.
const MENU_SVG_ICON_SIZE = 28

const Wrapper = styled.div`
  display: inline-flex;
  height: 100%;
  @media (min-width: ${props => props.collapseWidth}px) {
    display: none;
  }
`

const Toggle = styled.button`
  height: 100%;
  width: 56px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  border: none;
  border-radius: 4px;
  background-color: ${props => (props.isOpen ? themeColors.whiteTransparent01 : 'transparent')};
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background-color: ${themeColors.whiteTransparent01};
  }
  &:hover > i {
    color: ${UIStyles.whiteColor};
    transform: scale(1.1) translateY(-1px);
  }
`

const ToggleIcon = styled.i`
  font-size: 32px;
  color: ${props => (props.isOpen ? UIStyles.whiteColor : UIStyles.lightGrey)};
  transition: all 0.2s ease;
`

const Menu = styled.ul`
  position: fixed;
  top: ${TOP_BAR_HEIGHT}px;
  right: 0;
  z-index: 15;
  margin: 0;
  padding: 8px 0;
  list-style: none;
  width: 258px;
  background: ${UIStyles.blueColorOpacity};
  -webkit-backdrop-filter: ${surfaces.glassBackdropFilter};
  backdrop-filter: ${surfaces.glassBackdropFilter};
  border-bottom-left-radius: 12px;
  box-shadow: ${surfaces.dockedRightCardShadow};
`

// styled-components v3 forwards every prop of a wrapped component to it (and Link to the <a>), so
// the active state travels as a class, not a prop.
const MenuLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 24px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  line-height: 24px;
  font-weight: bold;
  letter-spacing: 1px;
  text-decoration: none;
  color: ${UIStyles.lightGrey};
  cursor: pointer;
  &:hover,
  &.${ACTIVE_CLASS} {
    color: ${UIStyles.whiteColor};
    background-color: ${themeColors.whiteTransparent01};
  }
  &.${ACTIVE_CLASS} {
    cursor: default;
  }
`

const MenuIconSlot = styled.span`
  width: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
`

// Another site's row: the same row, as a plain <a>.
const MenuAnchor = MenuLink.withComponent('a')

const MenuIcon = styled.i`
  font-size: 22px;
`

const ExternalMark = styled.i`
  margin-left: 8px;
  font-size: 11px;
`

// SVG icons draw in currentColor, so they follow the row's grey and its white on hover.
const renderMenuIcon = entry => {
  if (entry.mark) return <LayiMark active={entry.active} size={30} lift={false} />
  if (entry.svgIcon) {
    const Svg = entry.svgIcon
    return <Svg width={MENU_SVG_ICON_SIZE} height={MENU_SVG_ICON_SIZE} aria-hidden="true" focusable="false" />
  }
  return <MenuIcon className={entry.icon} />
}

// A page by its route, marked when it is the current one; another site in a new tab, never current.
const linkProps = (entry, generateUrl) =>
  entry.href
    ? { href: entry.href, target: '_blank', rel: 'noopener noreferrer' }
    : { to: generateUrl(entry.route), 'aria-current': entry.active ? 'page' : undefined }

export default function NavMenu({ entries, generateUrl, t, collapseWidth }) {
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef(null)
  const toggleRef = useRef(null)
  const close = () => setIsOpen(false)

  // An internal page navigates away; another site opens in a new tab and this page stays, so the
  // focus, which would drop to <body> with the unmounted row, goes back to the hamburger. No
  // preventDefault: the browser's own new-tab navigation must happen.
  const choose = entry => {
    if (entry.onClick) entry.onClick()
    close()
    if (entry.href && toggleRef.current) toggleRef.current.focus()
  }

  // A press outside the button and the menu closes it (the menu is fixed-positioned but still a
  // DOM descendant of the wrapper, so one `contains` covers both).
  useEffect(() => {
    if (!isOpen) return undefined
    const onPressOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) close()
    }
    document.addEventListener('mousedown', onPressOutside)
    document.addEventListener('touchstart', onPressOutside)
    return () => {
      document.removeEventListener('mousedown', onPressOutside)
      document.removeEventListener('touchstart', onPressOutside)
    }
  }, [isOpen])

  const onKeyDown = event => {
    if (event.key === 'Escape') close()
  }

  return (
    /* styled-components v3: innerRef hands over the DOM node (plain ref gives the wrapper) */
    <Wrapper id="NavMenu" innerRef={node => (wrapperRef.current = node)} onKeyDown={onKeyDown} collapseWidth={collapseWidth}>
      <Toggle
        innerRef={node => (toggleRef.current = node)}
        type="button"
        isOpen={isOpen}
        onClick={() => setIsOpen(previous => !previous)}
        aria-label={t('navigation_menu')}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        data-testid="nav-menu-toggle"
      >
        <ToggleIcon className="fa fa-bars" isOpen={isOpen} />
      </Toggle>

      {isOpen && (
        <Menu role="menu">
          {entries.map(entry => {
            const EntryLink = entry.href ? MenuAnchor : MenuLink
            return (
              <li key={entry.key} role="none">
                <EntryLink
                  className={entry.active ? `${NAV_LINK_CLASS} ${ACTIVE_CLASS}` : NAV_LINK_CLASS}
                  role="menuitem"
                  data-testid={`nav-menu-${entry.key}`}
                  onClick={() => choose(entry)}
                  {...linkProps(entry, generateUrl)}
                >
                  <MenuIconSlot>{renderMenuIcon(entry)}</MenuIconSlot>
                  <span>
                    {entry.title}
                    {entry.href && <ExternalMark className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />}
                  </span>
                </EntryLink>
              </li>
            )
          })}
        </Menu>
      )}
    </Wrapper>
  )
}
